import { useContext, createContext } from 'react';

import { Menu } from '@mui/material';

import type { HoverMenuProps, HoverSubmenuContextValue } from './types';

// ----------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

/**
 * Context for managing nested hover menus.
 * Allows child menu items to close all parent menus when an action is selected.
 */
export const HoverSubmenuContext = createContext<HoverSubmenuContextValue>({
  closeAll: noop,
});

export const useHoverSubmenuContext = () => useContext(HoverSubmenuContext);

// ----------------------------------------------------------------------

/**
 * A Menu component designed for hover-based interactions.
 *
 * This component wraps MUI's Menu and adds the necessary mouse event
 * handlers to work seamlessly with `useHoverMenu` hook and `HoverMenuItem`.
 *
 * @example
 * ```tsx
 * const { open, anchorEl, onOpen, onClose } = useHoverMenu();
 *
 * <HoverMenu
 *   open={open}
 *   anchorEl={anchorEl}
 *   onClose={onClose}
 *   onMouseEnter={onOpen}
 *   onMouseLeave={onClose}
 * >
 *   <MenuItem>Option 1</MenuItem>
 *   <MenuItem>Option 2</MenuItem>
 * </HoverMenu>
 * ```
 */
export function HoverMenu({
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
  children,
  slotProps,
  ...other
}: HoverMenuProps) {
  return (
    <Menu
      open={open}
      onClose={onClose}
      disableRestoreFocus
      hideBackdrop
      disableAutoFocusItem
      slotProps={{
        ...slotProps,
        paper: {
          ...slotProps?.paper,
          onMouseEnter,
          onMouseLeave,
        },
      }}
      sx={{
        pointerEvents: 'none',
        '& .MuiPaper-root': {
          pointerEvents: 'auto',
        },
      }}
      {...other}
    >
      {children}
    </Menu>
  );
}
